import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText, Sparkles, X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import DictationButton from "../../../components/DictationButton";
import { LoadingStatus } from "../../sessions/models/LoadingStatus";
import { extractImportProposal } from "../slice-extensions/assistant-import-slice-extension";
import { selectExtractionStatus, selectExtractStatus, selectImportStatusMessage } from "../assistant-import-slice";
import { ExtractionProgress } from "./ExtractionProgress";
import { ImportAttachment } from "../contracts/ImportAttachment";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif", "application/pdf"];
const MAX_FILE_BYTES = 10 * 1024 * 1024;

export function ImportSourceForm() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const extractStatus = useAppSelector(selectExtractStatus);
  const statusMessage = useAppSelector(selectImportStatusMessage);
  const extractionStatus = useAppSelector(selectExtractionStatus);

  const [text, setText] = useState("");
  const [attachment, setAttachment] = useState<(ImportAttachment & { fileName: string }) | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  // The extraction polls in the background; stop polling when the instructor leaves the page.
  const extraction = useRef<{ abort: () => void } | null>(null);
  useEffect(() => () => extraction.current?.abort(), []);

  const isExtracting = extractStatus === LoadingStatus.Loading;
  const canExtract = !isExtracting && (text.trim().length > 0 || attachment !== null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFileError(null);
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setFileError(t("assistantImport.source.unsupportedFile"));
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFileError(t("assistantImport.source.fileTooLarge"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.substring(dataUrl.indexOf(",") + 1);
      setAttachment({ mediaType: file.type, dataBase64: base64, fileName: file.name });
    };
    reader.readAsDataURL(file);
  };

  const handleExtract = () => {
    if (!canExtract) return;
    extraction.current = dispatch(
      extractImportProposal({
        text: text.trim().length > 0 ? text : null,
        attachment: attachment
          ? { mediaType: attachment.mediaType, dataBase64: attachment.dataBase64 }
          : null,
      }),
    );
  };

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="mb-4 flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-95 text-primary-50 dark:bg-primary-30 dark:text-primary-80">
          <Sparkles size={20} strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold leading-tight">
            {t("assistantImport.source.title")}
          </h2>
          <p className="text-sm text-grey-50 dark:text-grey-70 mt-0.5">
            {t("assistantImport.source.subtitle")}
          </p>
        </div>
      </div>

      <textarea
        className="input w-full min-h-[220px] font-mono text-sm"
        placeholder={t("assistantImport.source.placeholder")}
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={isExtracting}
      />

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label className="btn btn-secondary cursor-pointer">
          <FileText size={20} strokeWidth={2} />
          <span>{t("assistantImport.source.attachFile")}</span>
          <input
            type="file"
            className="hidden"
            accept={ACCEPTED_TYPES.join(",")}
            onChange={handleFileChange}
            disabled={isExtracting}
          />
        </label>
        <DictationButton
          onText={(spoken) => setText((prev) => (prev ? `${prev} ${spoken}` : spoken))}
          disabled={isExtracting}
        />
        {attachment && (
          <span className="flex items-center gap-2 rounded-full bg-primary-90 dark:bg-primary-30 px-3 py-1 text-sm text-primary-30 dark:text-primary-90">
            {attachment.fileName}
            <button
              type="button"
              aria-label={t("common.remove")}
              onClick={() => setAttachment(null)}
              disabled={isExtracting}
            >
              <X size={14} />
            </button>
          </span>
        )}
      </div>

      {(fileError || statusMessage) && (
        <div className="mt-4 rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
          {fileError ?? statusMessage}
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleExtract}
          disabled={!canExtract}
        >
          <Sparkles size={20} strokeWidth={2.5} />
          <span>
            {isExtracting
              ? t("assistantImport.source.extracting")
              : t("assistantImport.source.extract")}
          </span>
        </button>
      </div>
      {isExtracting && <ExtractionProgress status={extractionStatus} />}
    </div>
  );
}
